//@flow
import * as React from 'react';
import { PlanEditContext } from './PlanEditContext';
import { Box, Text, Columns, Button, Tooltip } from '@locus-taxy/neo-ui';
import { useTranslation } from 'react-i18next';

const EditToolbar = (): React.Node => {
    const {
        edits,
        undoEdit,
        redoEdit,
        editHistory,
        cleanEdits,
        editInProgress,
        saveEdits
    } = React.useContext(PlanEditContext);

    const { t } = useTranslation('entityManagement');

    const _getUndoTooltip = React.useCallback(() => {
        if (edits) {
            const lastEdit = edits[edits.length - 1];
            if (lastEdit) {
                if (lastEdit.targetBeatId) {
                    return t('fpPlanView.undoMoveVisitTooltip', {
                        visitId: lastEdit.visitId,
                        salesExecutiveId: lastEdit.targetBeatId
                    });
                }
                return t('fpPlanView.allocateVisitTooltip', {
                    visitId: lastEdit.visitId
                });
            }
        }
    }, [edits, t]);

    const _getRedoTooltip = React.useCallback(() => {
        if (editHistory) {
            const lastEdit = editHistory[editHistory.length - 1];
            if (lastEdit) {
                if (lastEdit && lastEdit.targetBeatId) {
                    return t('fpPlanView.moveVisitTooltip', {
                        visitId: lastEdit.visitId,
                        salesExecutiveId: lastEdit.targetBeatId
                    });
                }
                return t('fpPlanView.unallocateVisitTooltip', {
                    visitId: lastEdit.visitId
                });
            }
        }
    }, [editHistory, t]);

    if (!edits) {
        return null;
    }

    return (
        <Columns align="right" space="large">
            <Box width="1px" bg="grey.600" height={34} />
            <Box>
                <Text fontSize={2}>{edits.length} / 5</Text>
                <Text>{t('fpPlanView.unsavedEdits')}</Text>
            </Box>
            <Tooltip tooltip={_getUndoTooltip()}>
                <Button
                    icon="undo"
                    appearance="icon"
                    onClick={undoEdit}
                    disabled={!edits.length}
                />
            </Tooltip>
            <Tooltip tooltip={_getRedoTooltip()}>
                <Button
                    icon="redo"
                    appearance="icon"
                    onClick={redoEdit}
                    disabled={!editHistory?.length}
                />
            </Tooltip>
            <Box width="1px" bg="grey.600" height={34} />
            <Button
                palette="danger"
                onClick={cleanEdits}
                disabled={editInProgress}
            >
                {t('translations:cancel')}
            </Button>
            <Button
                disabled={editInProgress || !edits.length}
                palette="primary"
                appearance="solid"
                isLoading={editInProgress}
                onClick={saveEdits}
            >
                {t('fpPlanView.applyEdits')}
            </Button>
        </Columns>
    );
};

export { EditToolbar };
